import { useMemo } from 'react';
import { Copy, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useState } from 'react';
import type { HttpMethod } from '@/lib/integrations/types';

interface CurlGeneratorProps {
  method: HttpMethod;
  endpoint: string;
  alias: string;
  body?: string;
  baseUrl?: string;
}

export function generateCurl(
  method: HttpMethod,
  endpoint: string,
  alias: string,
  body?: string,
  baseUrl = import.meta.env.VITE_API_URL || 'http://localhost:4000'
): string {
  const normalizedEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
  const url = `${baseUrl}/api/v1/gateway/${alias}${normalizedEndpoint}`;

  const parts = [`curl -X ${method}`];
  parts.push(`  "${url}"`);
  parts.push(`  -H "Authorization: Bearer <your-token>"`);
  parts.push(`  -H "Content-Type: application/json"`);

  if (body && ['POST', 'PUT', 'PATCH'].includes(method)) {
    // Escape single quotes in body for shell
    const escapedBody = body.replace(/'/g, "'\\''");
    parts.push(`  -d '${escapedBody}'`);
  }

  return parts.join(' \\\n');
}

export function CurlGenerator({ method, endpoint, alias, body, baseUrl }: CurlGeneratorProps) {
  const [copied, setCopied] = useState(false);

  const curl = useMemo(
    () => generateCurl(method, endpoint, alias, body, baseUrl),
    [method, endpoint, alias, body, baseUrl]
  );

  const handleCopy = async () => {
    await navigator.clipboard.writeText(curl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="relative">
      <div className="absolute right-2 top-2">
        <Button
          variant="ghost"
          size="icon-sm"
          onClick={handleCopy}
          className="h-7 w-7"
        >
          {copied ? (
            <Check className="h-3.5 w-3.5 text-green-500" />
          ) : (
            <Copy className="h-3.5 w-3.5" />
          )}
        </Button>
      </div>
      <pre className="bg-muted rounded-lg p-4 pr-12 overflow-x-auto text-sm font-mono whitespace-pre-wrap break-all">
        <code>{curl}</code>
      </pre>
    </div>
  );
}

export default CurlGenerator;
